import { useEffect, useMemo } from 'react'
import {
  CategoriaNotificacaoLabels,
  CategoriaNotificacaoType,
  useDebounce,
} from '_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  Input,
  TableFilter,
  TableFilters,
} from 'app/components'
import {
  initialNotificacoesEnviadasQueryState,
  useQueryRequest,
} from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { EnviarNotificacaoButton } from '../../../components/EnviarNotificacaoButton'

const CATEGORIA_OPTIONS: IOption<CategoriaNotificacaoType>[] = Object.values(
  CategoriaNotificacaoType
).map((categoria) => ({ value: categoria, label: CategoriaNotificacaoLabels[categoria] }))

const PUSH_OPTIONS: IOption<boolean>[] = [
  { value: true, label: 'Push enviado' },
  { value: false, label: 'Sem push' },
]

/** A vírgula separa os critérios no `search` do backend — no texto digitado, ela quebraria a busca. */
const termo = (valor: string) => valor.replace(/,/g, ' ').trim()

export const NotificacoesEnviadasListHeader = () => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        termo(debouncedFilters.titulo) ? `titulo~*${termo(debouncedFilters.titulo)}*` : '',
        termo(debouncedFilters.destinatario)
          ? `pessoa.nome~*${termo(debouncedFilters.destinatario)}*`
          : '',
        debouncedFilters.categoria ? `categoria:${debouncedFilters.categoria.value}` : '',
        debouncedFilters.push ? `enviarPush:${debouncedFilters.push.value}` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialNotificacoesEnviadasQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    termo(filters.destinatario) && {
      key: 'destinatario',
      label: 'Destinatário',
      value: `“${termo(filters.destinatario)}”`,
      onRemove: () => setFilter('destinatario', ''),
    },
    termo(filters.titulo) && {
      key: 'titulo',
      label: 'Título',
      value: `“${termo(filters.titulo)}”`,
      onRemove: () => setFilter('titulo', ''),
    },
    filters.categoria && {
      key: 'categoria',
      label: 'Categoria',
      value: filters.categoria.label,
      onRemove: () => setFilter('categoria', undefined),
    },
    filters.push && {
      key: 'push',
      label: 'Push',
      value: filters.push.label,
      onRemove: () => setFilter('push', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <div className='card-header border-0 pt-6'>
      <TableFilters
        layout='stacked'
        actions={<EnviarNotificacaoButton />}
        footer={
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={clearFilters}
            summary={
              isLoading
                ? 'Buscando…'
                : `${total} ${total === 1 ? 'notificação encontrada' : 'notificações encontradas'}`
            }
          />
        }
      >
        <TableFilter label='Destinatário' htmlFor='filtro-notificacao-destinatario' wide>
          <Input
            id='filtro-notificacao-destinatario'
            placeholder='Buscar por nome'
            value={filters.destinatario}
            onChange={(e) => setFilter('destinatario', e.target.value)}
          />
        </TableFilter>
        <TableFilter label='Título' htmlFor='filtro-notificacao-titulo'>
          <Input
            id='filtro-notificacao-titulo'
            placeholder='Buscar por título'
            value={filters.titulo}
            onChange={(e) => setFilter('titulo', e.target.value)}
          />
        </TableFilter>
        <TableFilter label='Categoria' htmlFor='filtro-notificacao-categoria'>
          <CustomSelect
            inputId='filtro-notificacao-categoria'
            placeholder='Todas'
            options={CATEGORIA_OPTIONS}
            onChange={(categoria?: IOption<CategoriaNotificacaoType>) =>
              setFilter('categoria', categoria ?? undefined)
            }
            value={filters.categoria}
          />
        </TableFilter>
        <TableFilter label='Push' htmlFor='filtro-notificacao-push'>
          <CustomSelect
            inputId='filtro-notificacao-push'
            placeholder='Todos'
            options={PUSH_OPTIONS}
            onChange={(push?: IOption<boolean>) => setFilter('push', push ?? undefined)}
            value={filters.push}
          />
        </TableFilter>
      </TableFilters>
    </div>
  )
}
